import { Injectable } from '@angular/core';
import { offlineDb, type OutboxOp } from './offline-db';
import { type EnqueuePlan, replaceId } from './offline-writes';

/** Offline writes waiting to be sent, in the order they were made. */
@Injectable({
  providedIn: 'root',
})
export class OfflineOutbox {
  /** Ops still to send (rejected ones excluded), oldest first. */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async pending(loanKey: string): Promise<OutboxOp[]> {
    const ops = await (await offlineDb()).getAllFromIndex('outbox', 'byLoan', loanKey);
    return ops
      .filter((op) => op.failedStatus === undefined)
      .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async apply(plan: EnqueuePlan): Promise<void> {
    const tx = (await offlineDb()).transaction('outbox', 'readwrite');
    await Promise.all([
      ...plan.remove.map(async (seq) => tx.store.delete(seq)),
      plan.replace && tx.store.put(plan.replace),
      plan.add && tx.store.add(plan.add),
      tx.done,
    ]);
  }

  /** Drops a sent op and points the remaining ones to the id the server gave it. */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async complete(op: OutboxOp, serverId?: string): Promise<void> {
    const tx = (await offlineDb()).transaction('outbox', 'readwrite');
    const others = await tx.store.index('byLoan').getAll(op.loanKey);
    const { tempId } = op;
    await Promise.all([
      op.seq !== undefined && tx.store.delete(op.seq),
      ...(tempId && serverId
        ? others
            .filter((other) => other.seq !== op.seq)
            .map(async (other) => tx.store.put(replaceId(other, tempId, serverId)))
        : []),
      tx.done,
    ]);
  }

  /** Keeps an op the server rejected, out of the queue. */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async fail(op: OutboxOp, status: number): Promise<void> {
    await (await offlineDb()).put('outbox', { ...op, failedStatus: status });
  }
}
